/**
 * Converts an audio buffer from one sample rate to another using simple downsampling.
 * @param {Float32Array} inputBuffer - The input audio buffer.
 * @param {number} inputSampleRate - The sample rate of the input buffer.
 * @param {number} outputSampleRate - The desired sample rate for the output buffer.
 * @return {Float32Array} - The downsampled audio buffer.
 */
export const downsampleAudioBuffer = (
  inputBuffer,
  inputSampleRate,
  outputSampleRate
) => {
  if (inputSampleRate === outputSampleRate) return inputBuffer;

  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const outputLength = Math.round(inputBuffer.length / sampleRateRatio);
  const outputBuffer = new Float32Array(outputLength);

  let outputIndex = 0;
  let inputIndex = 0;

  while (outputIndex < outputLength) {
    const nextInputIndex = Math.round((outputIndex + 1) * sampleRateRatio);

    let sum = 0;
    let sampleCount = 0;

    for (
      let i = inputIndex;
      i < nextInputIndex && i < inputBuffer.length;
      i++
    ) {
      sum += inputBuffer[i];
      sampleCount++;
    }

    outputBuffer[outputIndex] = sampleCount > 0 ? sum / sampleCount : 0;

    inputIndex = nextInputIndex;
    outputIndex++;
  }

  return outputBuffer;
};

/**
 * Converts Float32 audio samples (-1 to 1) to 16-bit PCM format.
 * @param {Float32Array} floatBuffer - The input audio buffer with float samples.
 * @return {Int16Array} - The output audio buffer in 16-bit PCM format.
 */
export const convertFloat32ToPCM16 = (floatBuffer) => {
  const pcmBuffer = new Int16Array(floatBuffer.length);

  for (let i = 0; i < floatBuffer.length; i++) {
    const clampedSample = Math.max(-1, Math.min(1, floatBuffer[i]));

    pcmBuffer[i] =
      clampedSample < 0 ? clampedSample * 0x8000 : clampedSample * 0x7fff;
  }

  return pcmBuffer;
};

/**
 * Calculates RMS (Root Mean Square) of an audio buffer to determine its loudness.
 * @param {Float32Array} buffer - The input audio buffer.
 * @return {number} - The RMS value of the buffer.
 */
export const calculateRMS = (buffer) => {
  if (!buffer.length) return 0;

  let sumOfSquares = 0;

  for (const sample of buffer) {
    sumOfSquares += sample * sample;
  }

  return Math.sqrt(sumOfSquares / buffer.length);
};

/**
 * Extracts transcript text from the transcription payload.
 * @param {unknown} payload - Socket transcript payload.
 * @return {string} - The normalized transcript text.
 */
export const extractTranscriptText = (payload) => {
  if (!payload) return "";

  if (typeof payload === "string") {
    return payload.trim();
  }

  if (typeof payload?.transcript === "string") {
    return payload.transcript.trim();
  }

  return "";
};
